import { getResourceDefinition, IResourceValueRecord } from './definitions'
import { RESOURCE_OWNER_KEYS } from './definitions/ownerFormDefaults'
import { resolveResourceValue } from './resolveResourceValue'

export interface IResourceEditor {
  userId: string | null
  /** The editor's own company, from UserProject.getCompanyId. */
  companyId: string | null
  isAdmin: boolean
}

/**
 * Who may change an existing resource's properties: admins, its Responsible user, and its
 * Responsible company's responsible contacts or members. A resource type without owner
 * properties (locations, sensors) is admin-only, whatever its data says - mirroring the
 * backend's ResourceEditPermissionService.
 */
export const canEditResource = ({
  editor,
  record,
  resourceTypeId,
  getCompanyContactIds,
}: {
  editor: IResourceEditor
  record: IResourceValueRecord | null | undefined
  resourceTypeId: string | undefined
  getCompanyContactIds: (companyId: string) => readonly string[]
}): boolean => {
  if (editor.isAdmin) return true
  if (!record || !editor.userId || !hasOwners(resourceTypeId)) return false

  const responsibleId = resolveResourceValue({
    record,
    resourceTypeId,
    key: RESOURCE_OWNER_KEYS.user,
  })
  if (responsibleId && responsibleId === editor.userId) return true

  const companyId = resolveResourceValue({
    record,
    resourceTypeId,
    key: RESOURCE_OWNER_KEYS.company,
  })
  if (typeof companyId !== 'string' || !companyId) return false
  if (getCompanyContactIds(companyId).includes(editor.userId)) return true

  // Any member of the Responsible company. Deliberately broad for now; product intends
  // to narrow it, which is why it stays a separate branch.
  return companyId === editor.companyId
}

// The types that declare owner properties are the ones seeded with them on create.
const hasOwners = (resourceTypeId: string | undefined): boolean =>
  !!getResourceDefinition(resourceTypeId)?.form?.createDefaults?.[
    RESOURCE_OWNER_KEYS.user
  ]
